import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export function useQuotesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (quoteData: {
      id?: string;
      content: string;
      author?: string | null;
      source?: string | null;
      categories: string[];
    }) => {
      const { id, content, author, source, categories } = quoteData;
      
      try {
        if (id) {
          // Update existing quote
          const { data: updatedQuote, error: updateError } = await supabase
            .from("quotes")
            .update({
              content,
              author,
              source,
              updated_at: new Date().toISOString(),
            })
            .eq("id", id)
            .select()
            .single();

          if (updateError) throw updateError;

          // Delete existing categories
          const { error: deleteError } = await supabase
            .from("quote_categories")
            .delete()
            .eq("quote_id", id);

          if (deleteError) throw deleteError;

          // Insert new categories if any
          if (categories.length > 0) {
            const inserts = categories.map(category_id => ({
              quote_id: id,
              category_id,
            }));

            const { error: categoryError } = await supabase
              .from("quote_categories")
              .insert(inserts);

            if (categoryError) throw categoryError;
          }

          return updatedQuote;
        } else {
          // Create new quote
          const { data: newQuote, error: insertError } = await supabase
            .from("quotes")
            .insert([{ 
              content, 
              author: author || null, 
              source: source || null 
            }])
            .select()
            .single();

          if (insertError) throw insertError;
          if (!newQuote) throw new Error("Failed to create quote");

          // Insert categories if any
          if (categories.length > 0) {
            const inserts = categories.map(category_id => ({
              quote_id: newQuote.id,
              category_id,
            }));

            const { error: categoryError } = await supabase
              .from("quote_categories")
              .insert(inserts);

            if (categoryError) throw categoryError;
          }

          return newQuote;
        }
      } catch (error: any) {
        console.error('Mutation error:', error);
        throw error;
      }
    },
    onSuccess: (result, variables) => {
      const action = variables.id ? "updated" : "created";

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["quotes"] });
      
      toast({
        title: `Quote ${action}`,
        description: `The quote has been successfully ${action}.`,
      });
    },
    onError: (error: any) => {
      console.error('Mutation failed:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An error occurred while saving the quote.",
      });
    },
  });
}
